import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ArtistCard from '../components/ArtistCard';
import ArtistCardSkeleton from '../components/ArtistCardSkeleton';
import Testimonials from '../components/Testimonials';
import StatsCounter from '../components/StatsCounter';
import SEO from '../components/SEO';
// import CMSTestAct from '../components/CMSTestAct'; // Commented out - demo section removed
import { getHomePageActs } from '../data/dataLoader';
// import { getCMSActs } from '../data/dataLoader'; // Commented out - CMS demo removed
import './Home.css';

function Home() {
  const [popularArtists, setPopularArtists] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroImages, setHeroImages] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [cmsActsCount, setCmsActsCount] = useState(0); // Commented out - CMS demo removed

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const startTime = Date.now();
      const minLoadingTime = 500; // Minimum 500ms to show skeleton
      
      try {
        // Get a limited set of acts for the home page only
        const homeActs = await getHomePageActs(6);

        setPopularArtists(homeActs);

        // Use images from these acts for hero slideshow
        // Filter to get acts with images and take up to 5
        const actsWithImages = homeActs
          .filter(act => act.image && !act.image.includes('placeholder'))
          .slice(0, 5);
        
        if (actsWithImages.length > 0) {
          setHeroImages(actsWithImages.map(act => act.image));
        } else {
          // Fallback to default images if no acts available
          setHeroImages([
            '/images/abba-a-rival-quartet.png',
            '/images/forever-abba.png',
            '/images/super-troopers.png',
            '/images/gimme-abba.png'
          ]);
        }
      } catch (error) {
        console.error('❌ Error loading home page data:', error);
        // Fallback to default images on error
        setHeroImages([
          '/images/abba-a-rival-quartet.png',
          '/images/forever-abba.png',
          '/images/super-troopers.png',
          '/images/gimme-abba.png'
        ]);
      } finally {
        // Ensure minimum loading time for better UX
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, minLoadingTime - elapsed);
        setTimeout(() => {
          setLoading(false);
        }, remaining);
      }
    };

    loadData();
  }, []);

  // Hero slideshow effect - show each slide once then stop
  useEffect(() => {
    if (heroImages.length === 0) return;

    setCurrentSlide(0);

    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = prev + 1;

        // If we've reached the last slide, stop advancing
        if (next >= heroImages.length) {
          clearInterval(interval);
          return prev;
        }

        return next;
      });
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <div className="home-page">
      <SEO 
        title="Home"
        description="Scotland's premier entertainment agency. Book professional tribute acts, dinner speakers, and themed night experiences for weddings, corporate events, and private parties across Scotland."
        url="/"
        image="/images/scotbase-logo.png"
      />
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}
        </div>
        <div className="hero-content">
          <h1 className="hero-title">Scotland's Premier Entertainment Agency</h1>
          <p className="hero-subtitle">
            Book performance acts, dinner speakers, and themed entertainment across the UK
          </p>
          <div className="hero-buttons">
            <Link to="/artists" className="btn btn-primary">
              Browse Performance Acts
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Counter Section */}
      <StatsCounter />

      {/* CMS Test Section - Commented out for production */}
      {/* 
      {cmsActsCount > 0 && (
        <section className="cms-test-section">
          <div className="popular-artists-container">
            <h2>🚀 CMS Demo - {cmsActsCount} Act{cmsActsCount !== 1 ? 's' : ''} Loaded from CMS</h2>
            <CMSTestAct />
          </div>
        </section>
      )}
      */}

      {/* Featured Artist - Commented out for now */}
      {/* {loading ? (
        <section className="featured-artist-skeleton">
          <div className="featured-artist-skeleton-container">
            <div className="skeleton-featured-image"></div>
            <div className="skeleton-featured-content">
              <div className="skeleton-line skeleton-featured-title"></div>
              <div className="skeleton-line skeleton-featured-subtitle"></div>
              <div className="skeleton-line skeleton-featured-description"></div>
              <div className="skeleton-line skeleton-featured-description short"></div>
            </div>
          </div>
        </section>
      ) : (
        randomFeatured && <FeaturedArtist artist={randomFeatured} />
      )} */}

      {/* Popular Artists Section */}
      <section className="popular-artists">
        <div className="popular-artists-container">
          <h2>Featured</h2>
          {loading ? (
            <div className="artists-grid">
              {[...Array(6)].map((_, index) => (
                <ArtistCardSkeleton key={index} />
              ))}
            </div>
          ) : (
            <div className="artists-grid">
              {popularArtists.map(artist => (
                <ArtistCard key={artist.id} artist={artist} />
              ))}
            </div>
          )}
          <div className="view-all-container">
            <Link to="/artists" className="btn btn-primary">
              View All Performance Acts
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us">
        <h2>Why Choose Scotbase?</h2>
        <div className="features-grid">
          <div className="feature">
            <div className="feature-icon">🎭</div>
            <h3>Top Quality Acts</h3>
            <p>Handpicked professional tribute acts with years of experience</p>
          </div>
          <div className="feature">
            <div className="feature-icon">✅</div>
            <h3>Trusted Service</h3>
            <p>Thousands of successful bookings across Scotland and beyond</p>
          </div>
          <div className="feature">
            <div className="feature-icon">💼</div>
            <h3>All Events</h3>
            <p>Weddings, corporate events, festivals, parties and more</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🎵</div>
            <h3>Wide Variety</h3>
            <p>From rock legends to pop icons, we have acts for every taste</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Map Section */}
      <section className="map-section">
        <div className="map-container">
          <div className="map-content">
            <div className="map-info">
              <h2>Find Us</h2>
              <p className="map-description">Based in Paisley, serving clients across Scotland</p>
              <div className="contact-details">
                <div className="contact-item">
                  <span className="contact-icon">📍</span>
                  <div>
                    <strong>Address</strong>
                    <p>103 Abercorn Street<br />Paisley, PA3 4AT</p>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📞</span>
                  <div>
                    <strong>Phone</strong>
                    <p><a href="tel:01418490333">0141 849 0333</a></p>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">✉️</span>
                  <div>
                    <strong>Email</strong>
                    <p><a href="mailto:info@scotbase.net">info@scotbase.net</a></p>
                  </div>
                </div>
              </div>
            </div>
            <div className="map-embed">
              <iframe
                title="Scotbase Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2241.234567890123!2d-4.423456789012345!3d55.84567890123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x488846e234567890%3A0x1234567890abcdef!2s103%20Abercorn%20St%2C%20Paisley%20PA3%204AT!5e0!3m2!1sen!2suk!4v1234567890123!5m2!1sen!2suk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;

